import { useEffect, useCallback } from 'react';
import { Control, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useCallbackPrompt } from 'hooks';
import { VisionFormValues } from 'types';

interface UseVisionFormProps {
  mode: 'create' | 'edit';
  handleSubmit: (data: VisionFormValues) => void;
  defaultValues: VisionFormValues;
}

interface UseVisionFormReturn {
  control: Control<VisionFormValues>;
  isOpen: boolean;
  isLoading: boolean;
  goBack: () => void;
  handleFormSubmit: () => void;
  handleSubmitConfirmModal: () => void;
  handleCloseConfirmModal: () => void;
  isDirty: boolean;
}

export function useVisionForm({
  handleSubmit: submitForm,
  defaultValues,
  mode,
}: UseVisionFormProps): UseVisionFormReturn {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty, isSubmitted, errors },
  } = useForm<VisionFormValues>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const navigate = useNavigate();

  const { showPrompt, confirmNavigation, cancelNavigation } = useCallbackPrompt(
    isDirty && !isSubmitted,
  );

  const handleFormSubmit = handleSubmit(async (data) => {
    await submitForm(data);
  });

  async function handleSubmitConfirmModal() {
    confirmNavigation();
  }

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    if (isSubmitted && Object.keys(errors).length === 0) {
      goBack();
    }
  }, [isSubmitted, goBack, errors, mode]);

  return {
    control,
    handleFormSubmit,
    isOpen: showPrompt,
    handleSubmitConfirmModal,
    isLoading: isSubmitting,
    goBack,
    handleCloseConfirmModal: cancelNavigation,
    isDirty,
  };
}
